'use client';

import { useEffect, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { useSelector } from 'react-redux';
import { selectAdvancedSearchRadius } from 'src/redux/slices/discoveryMapSlice';
import { calculateCirclePolygon } from '../../utils/calculateCirclePolygon';

const METERS_PER_MILE = 1609.34;
const CIRCLE_POINTS = 64;

const isGoogleMapsLoaded = () =>
  typeof google !== 'undefined' && typeof google.maps !== 'undefined';

const AdvancedSearchRadiusLayer = () => {
  const map = useMap();
  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const advancedSearchRadius = useSelector(selectAdvancedSearchRadius);

  useEffect(() => {
    // Clean up if radius is cleared
    if (!advancedSearchRadius && polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
      return;
    }

    if (!map || !advancedSearchRadius?.center || !advancedSearchRadius?.radius_miles || !isGoogleMapsLoaded()) return;

    // Pan and zoom the map to frame the radius circle
    const { center, radius_miles } = advancedSearchRadius;
    map.panTo({ lat: center.lat, lng: center.lng });
    const zoom = Math.round(11 - Math.log2(radius_miles / 10));
    map.setZoom(Math.max(4, Math.min(zoom, 14)));

    // Clean up existing polygon
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }

    const radiusMeters = radius_miles * METERS_PER_MILE;
    const points = calculateCirclePolygon(
      advancedSearchRadius.center,
      radiusMeters,
      CIRCLE_POINTS
    );

    const path = points.map(
      (p) => new google.maps.LatLng(p.lat, p.lng)
    );

    const polygon = new google.maps.Polygon({
      paths: path,
      strokeColor: '#0284c7',
      strokeOpacity: 0.6,
      strokeWeight: 2,
      fillColor: '#0ea5e9',
      fillOpacity: 0.1,
      editable: false,
      draggable: false,
      clickable: false,
    });

    polygon.setMap(map);
    polygonRef.current = polygon;

    return () => {
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
    };
  }, [map, advancedSearchRadius]);

  return null;
};

export default AdvancedSearchRadiusLayer;
